/* eslint-disable no-param-reassign */
import { useEffect, useRef, useState } from 'react';
import { Check, Close } from 'style-guide/Icons';
import Image from 'style-guide/Image';
import Text from 'style-guide/Typography/Text';
import Title from 'style-guide/Typography/Title';

import imagePlaceholder from 'assets/images/imagePlaceholder.png';
import AssessmentCard from 'Components/InitialThoughtCard';
import RichEditor from 'Components/RichEditor';
import { useController } from 'react-hook-form';
import theme from 'style-guide/Theme';
import FeedbackSection from '../AssessmentCards/FeedbackSection';

const MultipleBlanksCard = ({ answer = [], question, control, index, assessmentInfo, answeredTest }) => {
  const { type, id: questionId, text, image, feedback } = assessmentInfo;
  const [initialBlanksCount, setInitialBlanksCount] = useState();
  const [blanksCount, setBlanksCount] = useState(0);

  const cardRef = useRef(null);

  const {
    field: { onChange },
  } = useController({ control, name: `answers.${index}` });

  const isCorrect = answeredTest?.correctAnswers?.[questionId]?.correct;
  const answersIds = answeredTest?.userAnswers?.[questionId];

  const allowDrop = (evt) => evt.preventDefault();

  const drag = (evt) => {
    evt.dataTransfer.setData('id', evt.target.id);
  };

  const drop = (evt) => {
    evt.preventDefault();
    const itemId = evt.dataTransfer.getData('id');
    const elem = document.getElementById(itemId);
    evt.target.replaceWith(elem);
    elem.setAttribute('draggable', false);

    elem.classList.add('filled-blank');
    setBlanksCount((prev) => prev + 1);
  };

  const handleClick = (e) => {
    const node = document.createElement('span');
    node.innerHTML = 'drop here';
    node.className = 'question-blank';
    node.ondragover = allowDrop;
    node.ondrop = drop;

    e.target.replaceWith(node);
    e.target.setAttribute('draggable', true);
    e.target.classList.add('grab');
    e.target.classList.remove('filled-blank');
    cardRef.current?.querySelector('.answers-container')?.append(e.target);
    setBlanksCount((prev) => prev - 1);
  };

  useEffect(() => {
    const count = question?.match(/<span/g)?.length || 0;
    setInitialBlanksCount(count);
  }, [question]);

  useEffect(() => {
    const filledBlanks = cardRef.current?.querySelectorAll(`.filled-blank`);
    filledBlanks.forEach((blank) => blank.addEventListener('click', handleClick));

    return () => {
      filledBlanks.forEach((blank) => blank.removeEventListener('click', handleClick));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const blanks = cardRef.current?.querySelectorAll('.blank');
    const learnerBlanks = cardRef.current?.querySelectorAll('.learner-blank');

    if (!answeredTest && Array.isArray(answer)) {
      blanks.forEach((el) => {
        el.ondragover = allowDrop;
        el.ondrop = drop;
        el.textContent = 'drop here';
        el.className = 'question-blank';
      });
      learnerBlanks.forEach((el) => {
        el.setAttribute('draggable', true);
        el.classList.add('grab');
        el.ondragstart = drag;
      });
    } else {
      answersIds?.forEach((id, i) => {
        const elem = document.getElementById(id);
        blanks[i]?.replaceWith(elem);
      });
    }
  }, [answer, answeredTest, answersIds]);

  useEffect(() => {
    if (blanksCount === initialBlanksCount) {
      const filledBlanks = Array.from(cardRef.current?.querySelectorAll('.filled-blank'));
      const idsArray = filledBlanks.map((el) => el.id);
      onChange({ type, questionId, answer: idsArray });
    }
  }, [blanksCount, initialBlanksCount, onChange, questionId, type]);

  return (
    <div ref={cardRef}>
      <AssessmentCard
        title={
          <>
            <Title className='card-title' variant={5}>
              <div dangerouslySetInnerHTML={{ __html: question }} />
            </Title>
            {answeredTest && (
              <div className='d-flex align-items-center'>
                {isCorrect ? (
                  <>
                    <Check className='check-icon' />
                    <Title variant={5} color={theme().colors.primary[600]}>
                      Correct
                    </Title>
                  </>
                ) : (
                  <>
                    <Close className='close-icon' />
                    <Title variant={5} color={theme().colors.error[600]}>
                      Incorrect
                    </Title>
                  </>
                )}
              </div>
            )}
          </>
        }
      >
        <div className='card-box'>
          <div className='d-flex flex-column'>
            {text && <RichEditor content={text} />}
            {image && (
              <div className='image-box'>
                <Image src={image} defaultImage={imagePlaceholder} className='image' alt='assessment' />
              </div>
            )}
            <div className='answers-container'>
              {Array.isArray(answer) &&
                answer?.map((el) => (
                  <Text key={el.id} $variant={1} className='learner-blank' id={el.id}>
                    {el?.answer}
                  </Text>
                ))}
            </div>
          </div>
        </div>
        {feedback && answeredTest && <FeedbackSection feedback={feedback} isCorrect={isCorrect} />}
      </AssessmentCard>
    </div>
  );
};

export default MultipleBlanksCard;
